import type { components } from '@altyor/api-contract';

export type DisputeReason = components['schemas']['DisputeReason'];

export const DISPUTE_REASON_LABEL_FR: Record<DisputeReason, string> = {
  LEGAL_INFORMATION_MISSING: 'Mentions légales manquantes',
  INCORRECT_RECIPIENT: 'Destinataire incorrect',
  EINVOICING_ADDRESS_INCORRECT: 'Adresse électronique incorrecte',
  UNKNOWN_TRANSACTION: 'Transaction inconnue',
  PO_REFERENCE_INCORRECT_OR_MISSING: 'Référence PO incorrecte ou manquante',
  CONTRACT_REFERENCE_MISSING: 'Référence contrat manquante',
  VAT_RATE_INCORRECT: 'Taux de TVA incorrect',
  TOTAL_AMOUNT_INCORRECT: 'Montant total incorrect',
  CALCULATION_ERROR: 'Erreur de calcul',
  DUPLICATE_INVOICE: 'Facture en doublon',
  DOUBLE_INVOICING: 'Double facturation',
  CONTRACT_TERMINATED: 'Contrat résilié',
  UNKNOWN_ISSUER: 'Émetteur inconnu',
  FINANCE_TERMS_INCORRECT: 'Conditions financières incorrectes',
  PRICES_INCORRECT: 'Prix incorrects',
  SIRET_INCORRECT_OR_MISSING: 'SIRET incorrect ou manquant',
  ROUTING_CODE_INCORRECT: 'Code de routage incorrect',
  REFERENCE_INCORRECT: 'Référence incorrecte',
  QUANTITY_INCORRECT: 'Quantité incorrecte',
  ITEM_INCORRECT: 'Article incorrect',
  ITEM_QUALITY_INSUFFICIENT: 'Qualité article insuffisante',
  DELIVERY_ISSUE: 'Problème de livraison',
  PAYMENT_TERMS_INCORRECT: 'Conditions de paiement incorrectes',
  UNIT_PRICE_INCORRECT: 'Prix unitaire incorrect',
  DISCOUNT_INCORRECT: 'Remise incorrecte',
  BANK_DETAILS_INCORRECT: 'Coordonnées bancaires incorrectes',
  MISSING_DOCUMENTS: 'Documents manquants',
  OTHER: 'Autre',
};

export interface DisputeGroup {
  id: string;
  label: string;
  reasons: DisputeReason[];
}

/**
 * UI-only grouping. BE persists only the enum value; this surfaces the
 * 28 motifs in a scannable list keyed to the comptable's mental model.
 */
export const DISPUTE_GROUPS: DisputeGroup[] = [
  {
    id: 'identity',
    label: 'Identité',
    reasons: [
      'LEGAL_INFORMATION_MISSING',
      'INCORRECT_RECIPIENT',
      'EINVOICING_ADDRESS_INCORRECT',
      'UNKNOWN_ISSUER',
      'SIRET_INCORRECT_OR_MISSING',
      'ROUTING_CODE_INCORRECT',
    ],
  },
  {
    id: 'amounts',
    label: 'Montants',
    reasons: [
      'VAT_RATE_INCORRECT',
      'TOTAL_AMOUNT_INCORRECT',
      'CALCULATION_ERROR',
      'PRICES_INCORRECT',
      'UNIT_PRICE_INCORRECT',
      'DISCOUNT_INCORRECT',
    ],
  },
  {
    id: 'reference',
    label: 'Référence',
    reasons: [
      'PO_REFERENCE_INCORRECT_OR_MISSING',
      'CONTRACT_REFERENCE_MISSING',
      'REFERENCE_INCORRECT',
    ],
  },
  {
    id: 'item-quantity',
    label: 'Article / Quantité',
    reasons: ['QUANTITY_INCORRECT', 'ITEM_INCORRECT', 'ITEM_QUALITY_INSUFFICIENT'],
  },
  { id: 'delivery', label: 'Livraison', reasons: ['DELIVERY_ISSUE'] },
  { id: 'duplicate', label: 'Doublon', reasons: ['DUPLICATE_INVOICE', 'DOUBLE_INVOICING'] },
  {
    id: 'contract',
    label: 'Contrat',
    reasons: ['CONTRACT_TERMINATED', 'UNKNOWN_TRANSACTION'],
  },
  {
    id: 'payment',
    label: 'Paiement',
    reasons: ['PAYMENT_TERMS_INCORRECT', 'FINANCE_TERMS_INCORRECT', 'BANK_DETAILS_INCORRECT'],
  },
  { id: 'documents', label: 'Documents', reasons: ['MISSING_DOCUMENTS'] },
  { id: 'other', label: 'Autre', reasons: ['OTHER'] },
];

export const ALL_DISPUTE_REASONS: DisputeReason[] = Object.keys(
  DISPUTE_REASON_LABEL_FR,
) as DisputeReason[];

// REQ-FE-033 area B8 — Hints ported verbatim from the mockup source at
// `.claude/worktrees/feat+web-vite-react-scaffold/apps/web/src/lib/dispute-reasons.ts`
// (REQ-FE-033 URGENT addendum). Each is a one-liner shown below the
// label in the motif card (≤120 chars to keep the 2-col grid balanced).
export const DISPUTE_REASON_DESCRIPTION_FR: Record<DisputeReason, string> = {
  VAT_RATE_INCORRECT: 'Le taux appliqué ne correspond pas au taux attendu.',
  TOTAL_AMOUNT_INCORRECT: 'Le total TTC ne correspond pas aux lignes ou au bon de commande.',
  CALCULATION_ERROR: 'Erreur arithmétique entre HT, TVA et TTC.',
  UNIT_PRICE_INCORRECT: 'Au moins un prix unitaire ne correspond pas au contrat.',
  PRICES_INCORRECT: 'Plusieurs lignes affichent des prix non contractuels.',
  DISCOUNT_INCORRECT: 'La remise appliquée diverge de la négociation.',
  FINANCE_TERMS_INCORRECT: 'Escompte, délai ou pénalités non conformes.',
  PO_REFERENCE_INCORRECT_OR_MISSING: "Le PO référencé n'existe pas ou est mal recopié.",
  CONTRACT_REFERENCE_MISSING: "Aucun contrat n'est rattaché à cette facture.",
  REFERENCE_INCORRECT: 'Une autre référence métier est erronée (BL, devis, etc.).',
  SIRET_INCORRECT_OR_MISSING: 'Le SIRET du destinataire ne correspond pas à la société.',
  ROUTING_CODE_INCORRECT: 'Le code de routage PDP/PA est erroné.',
  INCORRECT_RECIPIENT: 'Cette facture ne concerne pas notre société.',
  EINVOICING_ADDRESS_INCORRECT: "L'adresse PDP utilisée est obsolète.",
  UNKNOWN_ISSUER: "Aucune relation fournisseur existante n'est trouvée.",
  UNKNOWN_TRANSACTION: 'Aucune commande ou prestation ne correspond.',
  QUANTITY_INCORRECT: 'La quantité facturée ne correspond pas à la quantité reçue.',
  ITEM_INCORRECT: "Un article facturé ne correspond pas à l'article livré.",
  ITEM_QUALITY_INSUFFICIENT: 'Marchandise refusée pour non-conformité qualité.',
  DELIVERY_ISSUE: 'Livraison incomplète, hors délai ou non conforme.',
  DUPLICATE_INVOICE: 'Cette facture a déjà été reçue avec la même référence.',
  DOUBLE_INVOICING: 'Les prestations sont déjà facturées sur une autre pièce.',
  CONTRACT_TERMINATED: 'Le contrat couvrant cette prestation est terminé.',
  BANK_DETAILS_INCORRECT: "L'IBAN/BIC indiqué ne correspond pas au fournisseur connu.",
  PAYMENT_TERMS_INCORRECT: 'Le délai ou le mode de paiement diverge.',
  LEGAL_INFORMATION_MISSING: 'Mention obligatoire absente (RCS, capital, TVA intracom, etc.).',
  MISSING_DOCUMENTS: 'Pièces justificatives requises non fournies.',
  OTHER: "À utiliser uniquement si aucun motif ci-dessus ne s'applique.",
};
